import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../api/api";
import Modal from "../components/modal";

// 🎨 Paleta de Cores de Fabricantes (UX Visual Rápida)
const getBrandColor = (brandString) => {
  const brand = (brandString || "").toUpperCase();
  if (brand.includes("RICOH")) return "bg-red-50 text-red-600 border-red-200";
  if (brand.includes("KONICA") || brand.includes("MINOLTA")) return "bg-blue-50 text-blue-600 border-blue-200";
  if (brand.includes("SAMSUNG")) return "bg-sky-50 text-sky-700 border-sky-200";
  if (brand.includes("BROTHER")) return "bg-indigo-50 text-indigo-600 border-indigo-200";
  if (brand.includes("LEXMARK")) return "bg-emerald-50 text-emerald-600 border-emerald-200";
  if (brand.includes("HP")) return "bg-cyan-50 text-cyan-700 border-cyan-200";
  if (brand.includes("XEROX")) return "bg-rose-50 text-rose-600 border-rose-200";
  return "bg-slate-100 text-slate-600 border-slate-300"; 
};

export default function Laboratory() {
  const [printers, setPrinters] = useState([]);
  const navigate = useNavigate();
  
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [newPrinter, setNewPrinter] = useState({
    brand: "", model: "", serial_number: "", counter: 0, counter_color: 0,
    cylinder_status: "Bom", fuser_status: "Bom", belt_status: "Bom",
    developer_status: "Bom", rollers_status: "Bom", duplex_status: "Bom",
    diagnostic: "", notes: "", client_id: null 
  });

  const [swapData, setSwapData] = useState({
    cliente_id_saindo: "", printer_id_saindo: "", contador_final_bk_saindo: "", contador_final_color_saindo: "",
    printer_id_entrando: "", contador_inicial_bk_entrando: "", contador_inicial_color_entrando: "", notes: "" 
  });

  const fetchPrinters = async () => {
    try {
      const res = await api.get("/printers");
      const validP = Array.isArray(res.data) ? res.data : [];
      const uniquePrinters = Array.from(new Map(validP.map(item => [item.id, item])).values());
      setPrinters(uniquePrinters);
    } catch (err) { console.error("Erro ao sincronizar laboratório:", err); }
  };

  useEffect(() => { fetchPrinters(); }, []);

  // 🚀 AGRUPAMENTO E ORDENAÇÃO (Mantida Lógica Original)
  const groupedLabPrinters = useMemo(() => {
    const labOnly = printers.filter(p => p.client_id === null || p.client_name?.toLowerCase().includes("laboratório"));
    const groups = {};
    const term = searchTerm.toLowerCase().trim();

    labOnly.forEach(p => {
      const matchesSearch = 
        p.model?.toLowerCase().includes(term) || 
        p.brand?.toLowerCase().includes(term) ||
        p.serial?.toLowerCase().includes(term);

      if (term && !matchesSearch) return;

      const modelKey = p.model || "MODELO NÃO DEFINIDO";
      if (!groups[modelKey]) {
        groups[modelKey] = { model: modelKey, brand: p.brand || "OUTROS", units: [] };
      }
      groups[modelKey].units.push(p);
    });

    return Object.values(groups).sort((a, b) => {
      if (b.units.length !== a.units.length) {
        return b.units.length - a.units.length;
      }
      return a.model.localeCompare(b.model);
    });
  }, [printers, searchTerm]);

  const uniqueClients = useMemo(() => {
    const map = new Map();
    printers.filter(p => p.client_id !== null).forEach(p => {
        map.set(p.client_id, { id: p.client_id, name: p.client_name });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [printers]);

  const availablePrintersForClient = printers.filter(p => p.client_id === parseInt(swapData.cliente_id_saindo));

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newPrinter, counter: parseInt(newPrinter.counter) || 0, counter_color: parseInt(newPrinter.counter_color) || 0, client_id: null };
      await api.post("/printers", payload);
      alert("Ativo registrado no laboratório! 🚀");
      setIsAddModalOpen(false);
      setNewPrinter({ brand: "", model: "", serial_number: "", counter: 0, counter_color: 0, cylinder_status: "Bom", fuser_status: "Bom", belt_status: "Bom", developer_status: "Bom", rollers_status: "Bom", duplex_status: "Bom", diagnostic: "", notes: "", client_id: null });
      fetchPrinters();
    } catch (err) { alert("Erro ao cadastrar equipamento."); }
  };

  const handleSwapSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        printer_id_saindo: swapData.printer_id_saindo, 
        contador_final_saindo: Number(swapData.contador_final_bk_saindo) || 0, 
        contador_final_color_saindo: Number(swapData.contador_final_color_saindo) || 0,
        printer_id_entrando: swapData.printer_id_entrando, 
        contador_inicial_entrando: Number(swapData.contador_inicial_bk_entrando) || 0,
        contador_inicial_color_entrando: Number(swapData.contador_inicial_color_entrando) || 0,
        notes: swapData.notes 
      };
      await api.post("/printers/swap", payload);
      alert("Troca Logística concluída! ♻️");
      setIsSwapModalOpen(false);
      setSwapData({ cliente_id_saindo: "", printer_id_saindo: "", contador_final_bk_saindo: "", contador_final_color_saindo: "", printer_id_entrando: "", contador_inicial_bk_entrando: "", contador_inicial_color_entrando: "", notes: "" });
      fetchPrinters();
    } catch (err) { alert(`Falha na transação logística.`); }
  };

  const totalModels = groupedLabPrinters.length;
  const totalStock = groupedLabPrinters.reduce((acc, group) => acc + group.units.length, 0);

  return (
    <div className="p-2 md:p-8 min-h-screen bg-[#f1f5f9] animate-in fade-in duration-700 pb-24 lg:pb-8">
      
      {/* HEADER - RESPONSIVO */}
      <div className="max-w-[1600px] mx-auto mb-6 bg-white border border-slate-200 p-5 md:p-8 rounded-[20px] md:rounded-2xl shadow-sm flex flex-col xl:flex-row justify-between items-center gap-6">
        <div className="text-center xl:text-left">
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tighter uppercase flex items-center justify-center xl:justify-start gap-3">
            Estoque <span className="text-indigo-600">Laboratório</span>
          </h1>
          
          <div className="flex flex-wrap justify-center xl:justify-start gap-3 mt-4">
            <span className="bg-slate-100 border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2">
               {totalModels} Modelos
            </span>
            <span className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2">
               {totalStock} Unid. Prontas
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
          <div className="relative group w-full xl:w-80">
            <input 
              placeholder="Pesquisar..." 
              className="w-full p-3.5 pl-12 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:bg-white focus:border-indigo-400 transition-all uppercase"
              onChange={e => setSearchTerm(e.target.value)}
            />
            <span className="absolute left-4 top-3.5 text-sm opacity-40">🔍</span>
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <button onClick={() => setIsSwapModalOpen(true)} className="flex-1 sm:flex-none px-4 py-3.5 bg-slate-800 text-white font-black rounded-xl shadow-md text-[9px] uppercase tracking-widest hover:bg-slate-700 transition-all">
              🔄 Troca
            </button>
            <button onClick={() => setIsAddModalOpen(true)} className="flex-1 sm:flex-none px-4 py-3.5 bg-indigo-600 text-white font-black rounded-xl shadow-md text-[9px] uppercase tracking-widest hover:bg-indigo-700 transition-all">
              + Entrada
            </button>
          </div>
        </div>
      </div>

      {/* GRID DE CARDS - RESPONSIVO */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 items-start">
        {groupedLabPrinters.map((group) => (
          <div key={group.model} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:border-indigo-200 transition-all flex flex-col overflow-hidden group/card">
            <div className="p-4 md:p-5 border-b border-slate-100 flex justify-between items-start bg-gradient-to-b from-slate-50 to-white">
              <div className="overflow-hidden">
                <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${getBrandColor(group.brand)} inline-block mb-2`}>
                  {group.brand}
                </span>
                <h3 className="text-base md:text-lg font-black text-slate-900 tracking-tighter uppercase leading-tight truncate">{group.model}</h3>
              </div>
              <div className="bg-slate-900 text-white w-9 h-9 rounded-xl flex flex-col items-center justify-center shrink-0">
                <span className="text-xs font-black leading-none">{group.units.length}</span>
                <span className="text-[5px] uppercase font-bold opacity-70">UN</span>
              </div>
            </div>

            <div className="p-2 space-y-1.5 bg-slate-50/50">
              <div className={`p-1 space-y-2 overflow-y-auto custom-scrollbar ${group.units.length > 4 ? 'max-h-[220px]' : 'h-auto'}`}>
                {group.units.map(unit => (
                  <div key={unit.id} className="bg-white border border-slate-200 p-3 rounded-lg flex justify-between items-center hover:border-indigo-400 transition-colors group/item">
                    <div className="overflow-hidden pr-2">
                      <p className="text-[10px] font-mono font-bold text-slate-700 truncate">{unit.serial || "SEM SÉRIE"}</p>
                      <p className="text-[8px] font-black text-slate-400 mt-1 uppercase">ID: #{unit.id}</p>
                    </div>
                    <Link to={`/laboratory/${unit.id}`} className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all shrink-0 shadow-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {groupedLabPrinters.length === 0 && (
          <div className="col-span-full py-20 text-center bg-white border border-dashed border-slate-300 rounded-2xl">
            <p className="text-slate-400 font-black uppercase text-[10px] tracking-widest">Laboratório vazio</p>
          </div>
        )}
      </div>

      {/* MODAL ENTRADA - RESPONSIVO */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <div className="p-2 max-h-[85vh] overflow-y-auto custom-scrollbar">
          <div className="flex justify-between items-center mb-6 border-b border-slate-200 pb-4">
            <h2 className="text-xl font-black uppercase tracking-tighter text-slate-900">Entrada Lab</h2>
            <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-rose-500 text-2xl font-black">✕</button>
          </div>
          <form onSubmit={handleAddSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input required placeholder="MARCA" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-black uppercase text-xs" value={newPrinter.brand} onChange={e => setNewPrinter({...newPrinter, brand: e.target.value})} />
              <input required placeholder="MODELO" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-black uppercase text-xs" value={newPrinter.model} onChange={e => setNewPrinter({...newPrinter, model: e.target.value})} />
              <input required placeholder="S/N" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold uppercase text-xs" value={newPrinter.serial_number} onChange={e => setNewPrinter({...newPrinter, serial_number: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
              <input type="number" placeholder="BASE PB" className="w-full bg-white border border-slate-300 rounded-lg p-3 text-sm font-black text-center" value={newPrinter.counter} onChange={e => setNewPrinter({...newPrinter, counter: e.target.value})} />
              <input type="number" placeholder="BASE COLOR" className="w-full bg-white border border-indigo-200 rounded-lg p-3 text-sm font-black text-center text-indigo-600" value={newPrinter.counter_color} onChange={e => setNewPrinter({...newPrinter, counter_color: e.target.value})} />
            </div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
              <label className="text-[9px] font-black text-slate-400 uppercase mb-3 block text-center tracking-widest">Triagem Técnica</label>
              <div className="grid grid-cols-2 gap-2">
                {['cylinder', 'fuser', 'belt', 'developer', 'rollers', 'duplex'].map(id => (
                  <div key={id}>
                    <select className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-[9px] font-black uppercase" value={newPrinter[`${id}_status`]} onChange={e => setNewPrinter({...newPrinter, [`${id}_status`]: e.target.value})}>
                      <option value="Bom">{id.toUpperCase()}: BOM</option>
                      <option value="Trocar">TROCAR</option>
                      <option value="Avaliar">AVALIAR</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <textarea className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs h-20 outline-none" placeholder="Diagnóstico Inicial..." value={newPrinter.diagnostic} onChange={e => setNewPrinter({...newPrinter, diagnostic: e.target.value})} />
            <button type="submit" className="w-full py-4 bg-slate-900 text-white font-black rounded-xl text-[10px] uppercase tracking-widest active:scale-95">Registrar Entrada 🚀</button>
          </form>
        </div>
      </Modal>

      {/* MODAL TROCA (SWAP) - RESPONSIVO */}
      {isSwapModalOpen && (
         <div className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-md flex items-center justify-center p-2 sm:p-4">
             <div className="bg-white rounded-[25px] p-5 md:p-8 w-full max-w-4xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto custom-scrollbar">
              <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                  <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-slate-900">Troca Logística</h2>
                  <button onClick={() => setIsSwapModalOpen(false)} className="text-slate-400 hover:text-rose-500 text-3xl font-black transition-all">✕</button>
              </div>
              <form onSubmit={handleSwapSubmit} className="space-y-4">
                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100 space-y-3">
                  <h3 className="text-[9px] font-black text-rose-600 uppercase tracking-widest">📉 1. Retirada (Cliente)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select required className="w-full p-3 bg-white border border-rose-200 rounded-xl font-black text-[10px] uppercase" value={swapData.cliente_id_saindo} onChange={(e) => setSwapData({...swapData, cliente_id_saindo: e.target.value})}>
                        <option value="">CLIENTE...</option>
                        {uniqueClients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                    <select required disabled={!swapData.cliente_id_saindo} className="w-full p-3 bg-white border border-rose-200 rounded-xl font-black text-[10px] uppercase disabled:opacity-50" value={swapData.printer_id_saindo} onChange={(e) => setSwapData({...swapData, printer_id_saindo: e.target.value})}>
                        <option value="">MÁQUINA ATUAL...</option>
                        {availablePrintersForClient.map(p => (<option key={p.id} value={p.id}>{p.model} ({p.serial})</option>))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" required placeholder="FIM PB" className="w-full p-3 bg-white border border-rose-200 rounded-xl font-black text-center text-xs" value={swapData.contador_final_bk_saindo} onChange={(e) => setSwapData({...swapData, contador_final_bk_saindo: e.target.value})} />
                    <input type="number" placeholder="FIM COLOR" className="w-full p-3 bg-white border border-rose-200 rounded-xl font-black text-center text-xs" value={swapData.contador_final_color_saindo} onChange={(e) => setSwapData({...swapData, contador_final_color_saindo: e.target.value})} />
                  </div>
                </div>

                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 space-y-3">
                  <h3 className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">📈 2. Despacho (Novo)</h3>
                  <select required className="w-full p-3 bg-white border border-emerald-200 rounded-xl font-black text-[10px] uppercase" value={swapData.printer_id_entrando} onChange={(e) => setSwapData({...swapData, printer_id_entrando: e.target.value})}>
                      <option value="">MÁQUINA DO LAB...</option>
                      {groupedLabPrinters.flatMap(g => g.units).map(p => (<option key={p.id} value={p.id}>{p.model} ({p.serial})</option>))}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" required placeholder="INÍCIO PB" className="w-full p-3 bg-white border border-emerald-200 rounded-xl font-black text-center text-xs" value={swapData.contador_inicial_bk_entrando} onChange={(e) => setSwapData({...swapData, contador_inicial_bk_entrando: e.target.value})} />
                    <input type="number" placeholder="INÍCIO COLOR" className="w-full p-3 bg-white border border-emerald-200 rounded-xl font-black text-center text-xs" value={swapData.contador_inicial_color_entrando} onChange={(e) => setSwapData({...swapData, contador_inicial_color_entrando: e.target.value})} />
                  </div>
                </div>

                <textarea className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs h-20 outline-none" placeholder="Motivo da troca..." value={swapData.notes} onChange={(e) => setSwapData({...swapData, notes: e.target.value})} />
                <button type="submit" className="w-full py-4 bg-slate-900 text-white font-black rounded-xl text-[10px] uppercase tracking-widest active:scale-95">Confirmar Troca Logística ♻️</button>
              </form>
            </div>
         </div>
      )}
    </div>
  );
}